import React from "react";
import { useTranslation } from "react-i18next";
import { makeStyles } from "tss-react/mui";

import { Box, Card, CardContent, Theme, Typography } from "@mui/material";
import { alpha } from "@mui/material/styles";
import { PrimaryButton, SecondaryButton } from "@shared/CustomButton";
import EmteriaMdmIcon from "@shared/images/logoMdmApp.webp";

import { PageContainer } from "../../shared/components/PageContainer";

import { EnrollmentAndroidOsStepper } from "./EnrollmentAndroidOsStepper";

const useStyles = makeStyles()((theme: Theme) => ({
    wrapper: {
        display: "flex",
        flexDirection: "column",
        gap: theme.spacing(3),
    },
    container: {
        width: "100%",
        borderRadius: theme.spacing(2),
    },
    content: {
        flex: "0 0 auto",
        padding: theme.spacing(4),
        display: "flex",
        flexDirection: "column",
        gap: theme.spacing(4),
    },
    titleContainer: {
        display: "flex",
        justifyContent: "flex-start",
        alignItems: "flex-start",
    },
    title: {
        fontWeight: 600,
        marginBottom: theme.spacing(3),
        color: theme.palette.emteria.text.primary,
        textAlign: "left",
    },
    description: {
        color: theme.palette.emteria.text.secondary,
        marginBottom: theme.spacing(2),
    },
    stepper: {
        marginBottom: theme.spacing(1),
    },
    mdmBox: {
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        gap: theme.spacing(4),
        width: "100%",
    },
    iconCluster: {
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        gap: theme.spacing(0.25),
    },
    icon: {
        display: "block",
        width: 200,
        height: 200,
        objectFit: "contain",
    },
    appName: {
        fontWeight: 600,
        color: theme.palette.emteria.text.primary,
        textAlign: "center",
        lineHeight: 1.2,
        margin: 0,
        marginTop: -65,
    },
    stepsList: {
        display: "flex",
        flexDirection: "column",
        gap: theme.spacing(2),
        width: "100%",
        maxWidth: 480,
        alignSelf: "center",
    },
    step: {
        display: "flex",
        alignItems: "flex-start",
        gap: theme.spacing(2),
    },
    stepNumber: {
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        flexShrink: 0,
        width: 28,
        height: 28,
        borderRadius: "50%",
        backgroundColor: alpha(theme.palette.emteria.primary, 0.12),
        color: theme.palette.emteria.primary,
        fontWeight: 600,
        fontSize: 14,
    },
    stepText: {
        color: theme.palette.emteria.text.primary,
        paddingTop: theme.spacing(0.25),
    },
    actions: {
        padding: theme.spacing(3, 4),
        display: "flex",
        gap: theme.spacing(2),
        justifyContent: "space-between",
    },
    actionsLeft: {
        display: "flex",
        gap: theme.spacing(2),
    },
}));

interface ActivateDeviceStepProps {
    onSkip: () => void;
    onBack?: () => void;
    onContinue: () => void;
}

export const ActivateDeviceStep: React.FC<ActivateDeviceStepProps> = ({ onSkip, onBack, onContinue }) => {
    const { classes } = useStyles();
    const { t } = useTranslation(["modals", "common"]);

    const steps = [
        t("modals:enrollmentDialog.activateDeviceStep.steps.openApp"),
        t("modals:enrollmentDialog.activateDeviceStep.steps.enableManagement"),
    ];

    return (
        <PageContainer fullWidth={false} className={classes.wrapper}>
            <EnrollmentAndroidOsStepper activeStep={2} className={classes.stepper} />
            <Card className={classes.container} elevation={3}>
                <CardContent className={classes.content}>
                    <Box className={classes.titleContainer}>
                        <Typography variant="h6" className={classes.title}>
                            {t("modals:enrollmentDialog.activateDeviceStep.title")}
                        </Typography>
                    </Box>
                    <Typography variant="body1" className={classes.description}>
                        {t("modals:enrollmentDialog.activateDeviceStep.description")}
                    </Typography>

                    <Box className={classes.mdmBox}>
                        <Box className={classes.iconCluster}>
                            <img src={EmteriaMdmIcon} alt="" className={classes.icon} />
                            <Typography variant="subtitle1" className={classes.appName}>
                                {t("modals:enrollmentDialog.activateDeviceStep.appName")}
                            </Typography>
                        </Box>
                        <Box className={classes.stepsList}>
                            {steps.map((step, index) => (
                                <Box key={step} className={classes.step}>
                                    <Box className={classes.stepNumber}>{index + 1}</Box>
                                    <Typography variant="body1" className={classes.stepText}>
                                        {step}
                                    </Typography>
                                </Box>
                            ))}
                        </Box>
                    </Box>
                </CardContent>
                <Box className={classes.actions}>
                    <Box className={classes.actionsLeft}>
                        <SecondaryButton onClick={onBack || onSkip}>{t("common:button.goBack")}</SecondaryButton>
                    </Box>
                    <PrimaryButton onClick={onContinue}>
                        {t("modals:enrollmentDialog.activateDeviceStep.buttons.goToDevices")}
                    </PrimaryButton>
                </Box>
            </Card>
        </PageContainer>
    );
};
